/**
 * Streamlined Template Viewer - Simplified Template Selection & Preview
 *
 * A focused, visual approach to workflow template selection that emphasizes:
 * - Quick visual preview of template structure
 * - One-click instantiation with smart defaults
 * - Visual workflow flow representation
 * - Minimal cognitive load for users
 */

import React, { useState, useEffect, useMemo, Fragment } from 'react';
import {
  Stack,
  Group,
  Card,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Title,
  Paper,
  ActionIcon,
  Progress,
  Tabs,
  Alert,
  ThemeIcon,
  Divider,
} from '@mantine/core';
import {
  IconRocket,
  IconEye,
  IconSettings,
  IconCheck,
  IconClock,
  IconUsers,
  IconShield,
  IconCircleDot,
  IconArrowRight,
  IconSparkles,
  IconBolt,
  IconTarget,
} from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';

import {
  WorkflowTemplate,
  WorkflowCategory,
  WorkflowTemplateService,
} from '../templates/SystemWorkflowTemplates';

export interface StreamlinedTemplateViewerProps {
  onSelectTemplate: (template: WorkflowTemplate) => void;
  tenantId: string;
  userRoles: string[];
  previewMode?: boolean;
}

// Template visual representations
const TEMPLATE_VISUALS: Record<WorkflowCategory, {
  icon: React.ComponentType<any>;
  color: string;
  gradient: string;
  description: string;
}> = {
  [WorkflowCategory.UserManagement]: {
    icon: IconUsers,
    color: 'blue',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'User lifecycle workflows',
  },
  [WorkflowCategory.DocumentApproval]: {
    icon: IconCheck,
    color: 'green',
    gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    description: 'Document review & approval',
  },
  [WorkflowCategory.ComplianceReview]: {
    icon: IconShield,
    color: 'purple',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Regulatory compliance checks',
  },
  [WorkflowCategory.SystemIntegration]: {
    icon: IconSettings,
    color: 'orange',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    description: 'System & API integrations',
  },
  [WorkflowCategory.SecurityIncident]: {
    icon: IconBolt,
    color: 'red',
    gradient: 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)',
    description: 'Security incident response',
  },
  [WorkflowCategory.DataProcessing]: {
    icon: IconTarget,
    color: 'cyan',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Data processing workflows',
  },
  [WorkflowCategory.TenantOnboarding]: {
    icon: IconRocket,
    color: 'indigo',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Tenant onboarding processes',
  },
  [WorkflowCategory.PolicyManagement]: {
    icon: IconShield,
    color: 'violet',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Policy management workflows',
  },
  [WorkflowCategory.AuditProcess]: {
    icon: IconEye,
    color: 'teal',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'Audit and review processes',
  },
  [WorkflowCategory.BusinessProcess]: {
    icon: IconSparkles,
    color: 'gray',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    description: 'General business workflows',
  },
};

// Quick template cards for rapid selection
const QuickTemplateCard: React.FC<{
  template: WorkflowTemplate;
  onSelect: () => void;
  onPreview: () => void;
}> = ({ template, onSelect, onPreview }) => {
  const visual = TEMPLATE_VISUALS[template.category];
  const Icon = visual.icon;

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card
        shadow="md"
        padding="lg"
        radius="md"
        withBorder
        style={{
          cursor: 'pointer',
          background: `${visual.gradient}, white`,
          backgroundBlendMode: 'soft-light',
        }}
        onClick={onSelect}
      >
        <Stack gap="sm">
          {/* Header */}
          <Group justify="space-between" ta="left">
            <ThemeIcon
              size="xl"
              radius="md"
              variant="light"
              color={visual.color}
              style={{ background: 'rgba(255,255,255,0.9)' }}
            >
              <Icon size={24} />
            </ThemeIcon>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onPreview();
              }}
            >
              <IconEye size={16} />
            </ActionIcon>
          </Group>

          {/* Content */}
          <div>
            <Text fw={600} size="md" lineClamp={1}>
              {template.display_name}
            </Text>
            <Text size="sm" c="dimmed" lineClamp={2} mt={4}>
              {template.description}
            </Text>
          </div>

          {/* Metadata */}
          <Group gap="xs" justify="space-between">
            <Badge size="xs" variant="light" color={visual.color}>
              {template.parameters.length} fields
            </Badge>
            <Text size="xs" c="dimmed">v{template.version}</Text>
          </Group>

          {/* Quick launch button */}
          <Button
            fullWidth
            variant="filled"
            color={visual.color}
            size="sm"
            leftSection={<IconRocket size={16} />}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            Use Template
          </Button>
        </Stack>
      </Card>
    </motion.div>
  );
};

// Visual workflow preview
const WorkflowPreview: React.FC<{
  template: WorkflowTemplate;
  onClose: () => void;
  onUse: () => void;
}> = ({ template, onClose, onUse }) => {
  const visual = TEMPLATE_VISUALS[template.category];

  // Simulate workflow steps from template parameters
  const workflowSteps = useMemo(() => {
    const steps = ['Start'];

    // Add steps based on parameters
    template.parameters.forEach((param) => {
      if (param.required) {
        steps.push(`${param.display_name || param.name}`);
      }
    });

    steps.push('Complete');
    return steps;
  }, [template]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
    >
      <Paper shadow="xl" radius="md" p="xl" withBorder>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="md">
              <ThemeIcon size="lg" radius="md" color={visual.color}>
                <visual.icon size={24} />
              </ThemeIcon>
              <div>
                <Title order={3}>{template.display_name}</Title>
                <Text size="sm" c="dimmed">{visual.description}</Text>
              </div>
            </Group>
            <ActionIcon variant="subtle" onClick={onClose}>
              ×
            </ActionIcon>
          </Group>

          {/* Description */}
          <Text>{template.description}</Text>

          {/* Visual workflow flow */}
          <Paper p="md" withBorder radius="md" bg="gray.0">
            <Text size="sm" fw={500} mb="md">Workflow Flow Preview</Text>
            <Group gap="xs" ta="center">
              {workflowSteps.map((step, index) => (
                <Fragment key={step}>
                  <Group gap="xs" ta="center">
                    <ThemeIcon size="sm" radius="xl" color={visual.color} variant="light">
                      {index === 0 ? <IconCircleDot size={12} /> :
                       index === workflowSteps.length - 1 ? <IconTarget size={12} /> :
                       <IconClock size={12} />}
                    </ThemeIcon>
                    <Text size="xs" fw={500}>{step}</Text>
                  </Group>
                  {index < workflowSteps.length - 1 && (
                    <IconArrowRight size={14} color="gray" />
                  )}
                </Fragment>
              ))}
            </Group>
          </Paper>

          {/* Template details */}
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <Paper p="sm" withBorder radius="md" bg="blue.0">
              <Stack gap={4} ta="center">
                <Text size="lg" fw={600} c="blue">{template.parameters.length}</Text>
                <Text size="xs" ta="center" c="dimmed">Form Fields</Text>
              </Stack>
            </Paper>
            <Paper p="sm" withBorder radius="md" bg="green.0">
              <Stack gap={4} ta="center">
                <Text size="lg" fw={600} c="green">{template.compliance_frameworks.length}</Text>
                <Text size="xs" ta="center" c="dimmed">Compliance</Text>
              </Stack>
            </Paper>
            <Paper p="sm" withBorder radius="md" bg="purple.0">
              <Stack gap={4} ta="center">
                <Text size="lg" fw={600} c="purple">{template.required_roles.length}</Text>
                <Text size="xs" ta="center" c="dimmed">Required Roles</Text>
              </Stack>
            </Paper>
          </SimpleGrid>

          {/* Action buttons */}
          <Group justify="space-between">
            <Button variant="outline" onClick={onClose}>
              Back to Gallery
            </Button>
            <Button
              leftSection={<IconSparkles size={16} />}
              color={visual.color}
              onClick={onUse}
            >
              Create from Template
            </Button>
          </Group>
        </Stack>
      </Paper>
    </motion.div>
  );
};

export const StreamlinedTemplateViewer: React.FC<StreamlinedTemplateViewerProps> = ({
  onSelectTemplate,
  tenantId,
  previewMode = false,
}) => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<string>('recommended');

  const templateService = useMemo(() => new WorkflowTemplateService(), []);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const availableTemplates = await templateService.getAvailableWorkflows(tenantId);
        setTemplates(availableTemplates);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [templateService, tenantId]);

  // Categorize templates
  const categorizedTemplates = useMemo(() => {
    const categories = new Map<WorkflowCategory, WorkflowTemplate[]>();

    templates.forEach(template => {
      if (!categories.has(template.category)) {
        categories.set(template.category, []);
      }
      categories.get(template.category)!.push(template);
    });

    return categories;
  }, [templates]);

  // Recommended templates (simpler ones first)
  const recommendedTemplates = useMemo(() => {
    return templates
      .filter(t => t.parameters.length <= 5) // Simple templates
      .sort((a, b) => a.parameters.length - b.parameters.length)
      .slice(0, 6);
  }, [templates]);

  // Handle template selection
  const handleSelectTemplate = (template: WorkflowTemplate) => {
    if (previewMode) {
      setSelectedTemplate(template);
    } else {
      onSelectTemplate(template);
    }
  };

  const handleUseTemplate = (template: WorkflowTemplate) => {
    setSelectedTemplate(null);
    onSelectTemplate(template);
  };

  if (loading) {
    return (
      <Stack ta="center" gap="md" py="xl">
        <Text>Loading templates...</Text>
        <Progress value={70} w="50%" />
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <AnimatePresence mode="wait">
        {selectedTemplate ? (
          <WorkflowPreview
            key="preview"
            template={selectedTemplate}
            onClose={() => setSelectedTemplate(null)}
            onUse={() => handleUseTemplate(selectedTemplate)}
          />
        ) : (
          <motion.div
            key="gallery"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Stack gap="lg">
              {/* Header */}
              <div>
                <Group gap="md" mb="sm">
                  <ThemeIcon size="lg" radius="md" color="blue">
                    <IconSparkles size={24} />
                  </ThemeIcon>
                  <div>
                    <Title order={2}>Workflow Templates</Title>
                    <Text c="dimmed">Choose a template to get started quickly</Text>
                  </div>
                </Group>

                <Alert icon={<IconBolt size={16} />} color="blue" variant="light">
                  <Text size="sm">
                    <strong>{templates.length} templates</strong> available for your role.
                    All templates are production-ready and compliance-validated.
                  </Text>
                </Alert>
              </div>

              {/* Template tabs */}
              <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value || 'recommended')}>
                <Tabs.List>
                  <Tabs.Tab value="recommended" leftSection={<IconTarget size={16} />}>
                    Recommended
                  </Tabs.Tab>
                  <Tabs.Tab value="all" leftSection={<IconSettings size={16} />}>
                    All Categories
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="recommended" pt="lg">
                  <Stack gap="md">
                    <Text size="sm" c="dimmed">
                      Quick-start templates with minimal configuration required
                    </Text>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
                      {recommendedTemplates.map(template => (
                        <QuickTemplateCard
                          key={template.id}
                          template={template}
                          onSelect={() => handleSelectTemplate(template)}
                          onPreview={() => setSelectedTemplate(template)}
                        />
                      ))}
                    </SimpleGrid>
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="all" pt="lg">
                  <Stack gap="xl">
                    {Array.from(categorizedTemplates.entries()).map(([category, categoryTemplates]) => {
                      const visual = TEMPLATE_VISUALS[category];

                      return (
                        <div key={category}>
                          <Group gap="md" mb="md">
                            <ThemeIcon size="md" radius="md" color={visual.color}>
                              <visual.icon size={20} />
                            </ThemeIcon>
                            <div>
                              <Text fw={600}>{category.replace(/([A-Z])/g, ' $1').trim()}</Text>
                              <Text size="sm" c="dimmed">{visual.description}</Text>
                            </div>
                            <Badge variant="light" color={visual.color}>
                              {categoryTemplates.length}
                            </Badge>
                          </Group>

                          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" mb="xl">
                            {categoryTemplates.map(template => (
                              <QuickTemplateCard
                                key={template.id}
                                template={template}
                                onSelect={() => handleSelectTemplate(template)}
                                onPreview={() => setSelectedTemplate(template)}
                              />
                            ))}
                          </SimpleGrid>

                          <Divider />
                        </div>
                      );
                    })}
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
};

export default StreamlinedTemplateViewer;